import React, { useEffect, useMemo, useState } from 'react';
import apiService from '../services/api';

const Data = () => {
  const [loading, setLoading] = useState(false);
  const [tables, setTables] = useState([]);
  const [error, setError] = useState(null);

  const [activeExpirySymbol, setActiveExpirySymbol] = useState('nifty');
  const [expiries, setExpiries] = useState([]);
  const [expiryLoading, setExpiryLoading] = useState(false);
  const [expiryError, setExpiryError] = useState(null);

  const [newExpiry, setNewExpiry] = useState('');
  const [newWm, setNewWm] = useState('w');
  const [editOldExpiry, setEditOldExpiry] = useState('');
  const [editNewExpiry, setEditNewExpiry] = useState('');
  const [editWm, setEditWm] = useState('');

  const expiryTables = useMemo(() => {
    return (tables || []).filter(t => t.category === 'expiries');
  }, [tables]);

  const loadOverview = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await apiService.data.getOverview();
      setTables(resp?.data?.tables || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const loadExpiries = async (symbol) => {
    setExpiryLoading(true);
    setExpiryError(null);
    try {
      const resp = await apiService.data.getExpiries(symbol, 50);
      setExpiries(resp?.data?.expiries || []);
    } catch (e) {
      setExpiryError(e?.response?.data?.detail || e?.message || 'Failed to load expiries');
    } finally {
      setExpiryLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
  }, []);

  useEffect(() => {
    loadExpiries(activeExpirySymbol);
  }, [activeExpirySymbol]);

  const handleAddExpiry = async () => {
    if (!newExpiry) return;
    setExpiryError(null);
    try {
      await apiService.data.addExpiry(activeExpirySymbol, newExpiry, newWm || null);
      setNewExpiry('');
      setNewWm('w');
      await loadExpiries(activeExpirySymbol);
    } catch (e) {
      setExpiryError(e?.response?.data?.detail || e?.message || 'Failed to add expiry');
    }
  };

  const handleUpdateExpiry = async () => {
    if (!editOldExpiry || !editNewExpiry) return;
    setExpiryError(null);
    try {
      await apiService.data.updateExpiry(activeExpirySymbol, editOldExpiry, editNewExpiry, editWm || null);
      setEditOldExpiry('');
      setEditNewExpiry('');
      setEditWm('');
      await loadExpiries(activeExpirySymbol);
    } catch (e) {
      setExpiryError(e?.response?.data?.detail || e?.message || 'Failed to update expiry');
    }
  };

  const handleDeleteExpiry = async (expiry) => {
    if (!expiry) return;
    const ok = window.confirm(`Delete expiry ${expiry} from expiries_${activeExpirySymbol}?`);
    if (!ok) return;

    setExpiryError(null);
    try {
      await apiService.data.deleteExpiry(activeExpirySymbol, expiry);
      await loadExpiries(activeExpirySymbol);
    } catch (e) {
      setExpiryError(e?.response?.data?.detail || e?.message || 'Failed to delete expiry');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Data</h2>
        <button
          onClick={loadOverview}
          className="px-3 py-2 text-sm rounded bg-primary-700 text-white hover:bg-primary-800"
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="p-3 mb-4 rounded bg-red-50 text-red-700 text-sm">{error}</div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden mb-6">
        <div className="px-4 py-3 border-b">
          <h3 className="text-md font-medium text-gray-900">Tables Overview</h3>
          <div className="text-xs text-gray-500 mt-1">
            Latest timestamp is shown for tables having a <code>timestamp</code> column.
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Table</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Latest Timestamp</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr><td className="px-4 py-3 text-sm text-gray-500" colSpan={3}>Loading...</td></tr>
              ) : (tables || []).length === 0 ? (
                <tr><td className="px-4 py-3 text-sm text-gray-500" colSpan={3}>No tables found.</td></tr>
              ) : (
                (tables || []).map((t, idx) => (
                  <tr key={`${t.name}-${idx}`} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm text-gray-900 font-medium">{t.name}</td>
                    <td className="px-4 py-2 text-sm text-gray-700">{t.category}</td>
                    <td className="px-4 py-2 text-sm text-gray-700">{t.latest_timestamp || <span className="text-gray-400">—</span>}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b">
          <h3 className="text-md font-medium text-gray-900">Manage Expiries</h3>
          <div className="text-xs text-gray-500 mt-1">Add, edit, or delete expiries in <code>expiries_*</code> tables.</div>
        </div>

        <div className="p-4">
          {expiryError && (
            <div className="p-3 mb-4 rounded bg-red-50 text-red-700 text-sm">{expiryError}</div>
          )}

          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Symbol</label>
              <select
                value={activeExpirySymbol}
                onChange={(e) => setActiveExpirySymbol(e.target.value)}
                className="border rounded px-2 py-2 text-sm"
              >
                <option value="nifty">nifty</option>
                <option value="banknifty">banknifty</option>
                <option value="finnifty">finnifty</option>
              </select>
            </div>

            <div className="flex-1 min-w-[220px]">
              <label className="block text-xs text-gray-500 mb-1">Add expiry (YYYY-MM-DD)</label>
              <div className="flex gap-2">
                <input
                  value={newExpiry}
                  onChange={(e) => setNewExpiry(e.target.value)}
                  placeholder="2026-01-29"
                  className="border rounded px-2 py-2 text-sm w-full"
                />
                <select
                  value={newWm}
                  onChange={(e) => setNewWm(e.target.value)}
                  className="border rounded px-2 py-2 text-sm"
                >
                  <option value="w">w</option>
                  <option value="m">m</option>
                </select>
                <button
                  onClick={handleAddExpiry}
                  className="px-3 py-2 text-sm rounded bg-primary-700 text-white hover:bg-primary-800"
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-3 mb-6">
            <div className="flex-1 min-w-[220px]">
              <label className="block text-xs text-gray-500 mb-1">Edit old expiry (YYYY-MM-DD)</label>
              <input
                value={editOldExpiry}
                onChange={(e) => setEditOldExpiry(e.target.value)}
                placeholder="2026-01-23"
                className="border rounded px-2 py-2 text-sm w-full"
              />
            </div>
            <div className="flex-1 min-w-[220px]">
              <label className="block text-xs text-gray-500 mb-1">Edit new expiry (YYYY-MM-DD)</label>
              <div className="flex gap-2">
                <input
                  value={editNewExpiry}
                  onChange={(e) => setEditNewExpiry(e.target.value)}
                  placeholder="2026-01-30"
                  className="border rounded px-2 py-2 text-sm w-full"
                />
                <select
                  value={editWm}
                  onChange={(e) => setEditWm(e.target.value)}
                  className="border rounded px-2 py-2 text-sm"
                >
                  <option value="">wm</option>
                  <option value="w">w</option>
                  <option value="m">m</option>
                </select>
                <button
                  onClick={handleUpdateExpiry}
                  className="px-3 py-2 text-sm rounded bg-primary-700 text-white hover:bg-primary-800"
                >
                  Update
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-gray-700">
              Table: <code>expiries_{activeExpirySymbol}</code>
            </div>
            <button
              onClick={() => loadExpiries(activeExpirySymbol)}
              className="px-3 py-2 text-sm rounded bg-gray-100 text-gray-800 hover:bg-gray-200"
              disabled={expiryLoading}
            >
              Refresh Expiries
            </button>
          </div>

          <div className="border rounded overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Expiry</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">wm</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {expiryLoading ? (
                  <tr><td className="px-4 py-3 text-sm text-gray-500" colSpan={3}>Loading...</td></tr>
                ) : (expiries || []).length === 0 ? (
                  <tr><td className="px-4 py-3 text-sm text-gray-500" colSpan={3}>No expiries found.</td></tr>
                ) : (
                  (expiries || []).map((row) => (
                    <tr key={`${row.expiry}-${row.wm || ''}`} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm text-gray-900">{row.expiry}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">{row.wm || <span className="text-gray-400">—</span>}</td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => handleDeleteExpiry(row.expiry)}
                          className="px-3 py-1 text-xs rounded bg-red-50 text-red-700 hover:bg-red-100"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {expiryTables.length > 0 && (
            <div className="text-xs text-gray-500 mt-3">
              Found expiry tables: {expiryTables.map(t => t.name).join(', ')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Data;
